import axios from 'axios';

const baseURL = import.meta.env.VITE_WIX_APP_GB_API_URL ?? 'https://givebutter.test:3000';

export type WidgetType = 'button' | 'giving-form' | 'goal-bar' | 'signup-form';

export interface DecodedInstance {
  instance_id: string;
  is_connected: boolean;
  is_linked: boolean;
  account_id: number | null;
  account_aid: string | null;
  account_name: string | null;
  site?: { site_id?: string; site_name?: string };
}

export interface WixAccount {
  id: number;
  name: string;
  aid: string;
}

export interface WixCampaign {
  id: number;
  code: string;
  title: string;
  type: string;
}

export interface WixElement {
  id: number;
  display_id: string;
  type: WidgetType;
  name: string;
  status: 'active' | 'hidden' | 'disabled';
  is_default: boolean;
  campaign_id: number | null;
  campaign: { id: number; code: string; title: string } | null;
  config: Record<string, unknown>;
  deleted_at: string | null;
}

export interface FormSchemaEntry {
  group?: string;
  title?: string;
  placeholder?: string;
  type: string;
  default?: unknown;
  value?: unknown;
  options?: Record<string, string> | string[];
  min?: number;
  max?: number;
  step?: number;
  validate?: string[];
}

export type FormSchema = Record<string, FormSchemaEntry>;

export interface ElementOptions {
  types: WidgetType[];
  forms: Partial<Record<WidgetType, FormSchema>>;
}

export interface WixElementWithForm extends WixElement {
  form?: FormSchema;
}

export interface CreateElementInput {
  type: WidgetType;
  campaign_id?: number;
  name?: string;
  config?: Record<string, unknown>;
}

export interface UpdateElementInput {
  campaign_id?: number;
  name?: string;
  status?: 'active' | 'hidden' | 'disabled';
  config?: Record<string, unknown>;
}

export const createApi = (getAccessToken: () => Promise<string>) => {
  const client = axios.create({ baseURL });

  client.interceptors.request.use(async config => {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return {
    decodeInstance: async (): Promise<DecodedInstance> => {
      const { data } = await client.post<DecodedInstance>('/apps/wix/api/instance/decode');
      return data;
    },
    startConnect: async (): Promise<string> => {
      const { data } = await client.post<{ authorize_url: string }>('/apps/wix/api/oauth/connect');
      return data.authorize_url;
    },
    listAccounts: async (): Promise<{
      accounts: WixAccount[];
      selected_account_id: number | null;
    }> => {
      const { data } = await client.get<{
        accounts: WixAccount[];
        selected_account_id: number | null;
      }>('/apps/wix/api/accounts');
      return data;
    },
    selectAccount: async (accountId: number): Promise<DecodedInstance> => {
      const { data } = await client.post<DecodedInstance>('/apps/wix/api/account', {
        account_id: accountId,
      });
      return data;
    },
    listCampaigns: async (): Promise<WixCampaign[]> => {
      const { data } = await client.get<{ campaigns: WixCampaign[] }>('/apps/wix/api/campaigns');
      return data.campaigns;
    },
    listElements: async (): Promise<WixElement[]> => {
      const { data } = await client.get<{ elements: WixElement[] }>('/apps/wix/api/elements');
      return data.elements;
    },
    getElement: async (displayId: string): Promise<WixElementWithForm> => {
      const { data } = await client.get<WixElementWithForm>(`/apps/wix/api/elements/${displayId}`);
      return data;
    },
    getElementOptions: async (): Promise<ElementOptions> => {
      const { data } = await client.get<ElementOptions>('/apps/wix/api/elements/options');
      return data;
    },
    createElement: async (input: CreateElementInput): Promise<WixElement> => {
      const { data } = await client.post<WixElement>('/apps/wix/api/elements', input);
      return data;
    },
    updateElement: async (
      displayId: string,
      input: UpdateElementInput,
    ): Promise<WixElementWithForm> => {
      const { data } = await client.put<WixElementWithForm>(
        `/apps/wix/api/elements/${displayId}`,
        input,
      );
      return data;
    },
    deleteElement: async (displayId: string): Promise<void> => {
      await client.delete(`/apps/wix/api/elements/${displayId}`);
    },
    disconnect: async (): Promise<void> => {
      await client.post('/apps/wix/api/disconnect');
    },
  };
};
